/**
 * Feature flags for Phase 4 and experimental features.
 *
 * Profitability defaults to the zeecore truth layer. Set VITE_PROFIT_DATA_SOURCE=batch
 * to use the legacy CSV/S3 pipeline (local dev without zeecore).
 */

export type ProfitabilityDataSource = 'batch' | 'truth';
export type UtilisationDataSource = 'batch' | 'truth';

export interface FeatureFlags {
  /**
   * Profitability data source for Profit Agent and Report Data tab.
   * 
   * - 'truth': Use zeecore /profitability/v2/* (canonical events, default)
   * - 'batch': Use server /api/profitability/* (legacy CSV pipeline)
   */
  profitAgentDataSource: ProfitabilityDataSource;

  /**
   * Show parity banner when both batch and truth data available.
   * Only relevant when profitAgentDataSource='truth'.
   */
  showProfitabilityParityBanner: boolean;

  /**
   * Utilisation data source for Profit Report Util % column.
   * - 'truth': zeecore /utilisation/v2/* (default)
   * - 'batch': hide truth utilisation column
   */
  utilisationDataSource: UtilisationDataSource;
}

/**
 * Feature flags (can be overridden via environment or API config).
 */
export const FEATURE_FLAGS: FeatureFlags = {
  profitAgentDataSource: (import.meta.env.VITE_PROFIT_DATA_SOURCE as ProfitabilityDataSource) || 'truth',
  showProfitabilityParityBanner: import.meta.env.VITE_SHOW_PROFITABILITY_PARITY_BANNER === 'true',
  utilisationDataSource: (import.meta.env.VITE_UTIL_DATA_SOURCE as UtilisationDataSource) || 'truth',
};

/**
 * Check if truth layer is enabled for profitability.
 */
export function useTruthProfitability(): boolean {
  return FEATURE_FLAGS.profitAgentDataSource === 'truth';
}

export function useTruthUtilisation(): boolean {
  return FEATURE_FLAGS.utilisationDataSource === 'truth';
}
